import React from 'react';
import moment from 'moment';
import {
  Drawer,
  Button,
  Input,
  Row,
  Col,
  message,
  Divider,
  Spin,
  Upload,
} from 'antd';
import { CloseOutlined, DeleteOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { ReceivablesRequestActionStyles } from './Receivables.actionDrawerStyles';
import { StylesProps } from 'src/library/types';
import withStyles from 'react-jss';
import { getS3Url, putImageS3, submitInvoice } from './Receivables.api';
import { get } from 'lodash';
import { actionTypeAndModalTitleMapping } from './Receivables.utils';
import { formatDateTime, formatNumber, capitalize, transformName } from 'src/utils/utils';

const { Dragger } = Upload;
interface IProps extends StylesProps<ReturnType<typeof ReceivablesRequestActionStyles>> {
  isVisible: boolean;
  actionType: string;
  data: any;
  handleClose: (onSuccess?: boolean) => void;
  handleReload: () => void;
  columnsList: any[];
}

const RaiseInvoiceActionDrawer = (props: IProps): React.ReactElement => {
  const {
    classes,
    data,
    isVisible,
    handleClose,
    handleReload,
    actionType,
    columnsList = [],
  } = props;

  const [isLoading, setIsLoading] = React.useState(false);
  const [hideUploadModal, setHideUploadModal] = React.useState(false);
  const [signedURL, setSignedURL] = React.useState(undefined);
  const [unsignedURL, setUnsignedURL] = React.useState(undefined);
  const [fileList, setFileList] = React.useState([]);

  const columnsToShow = columnsList?.filter(
    (col) =>
      ![
        'actions',
        'status',
        'created_at',
        'object_type',
        'object_type_info',
        'invoice_number',
        'billing_cycle',
      ].includes(col.column_id),
  );
  const uploadPDFCallBack = async (file) => {
    setIsLoading(true);

    const response = await getS3Url({
      invoice_id: data?.id || `INVOICE_${formatDateTime(moment(), 'YYYYMMDDHHmmss')}`,
    });
    if (response.errorMessage) {
      message.error(response.errorMessage);
      setIsLoading(false);
      return;
    }

    const urls = response?.data;
    if (urls.imageSignedUrl && urls.imageUrl) {
      const uploadResponse = await putImageS3(urls.imageSignedUrl, file, 'application/pdf');
      if (uploadResponse.errorMessage) {
        message.error(uploadResponse.errorMessage);
        setIsLoading(false);
        setHideUploadModal(true);
        return;
      }
    }
    setSignedURL(urls.imageSignedUrl);
    setUnsignedURL(urls.imageUrl);
    setFileList([file]);
    setIsLoading(false);
  };

  const handleRemoveUpload = () => {
    setSignedURL(undefined);
    setUnsignedURL(undefined);
    setHideUploadModal(false);
    setFileList([]);
  };

  const handleRaiseInvoice = async () => {
    try {
      if (!signedURL) {
        message.error('Please upload invoice');
        return;
      }
      setIsLoading(true);

      const body = {
        id: String(data?.id),
        raised_invoice_pdf_url: unsignedURL,
        action_type: 'raise_invoice',
      };
      const response = await submitInvoice(body);
      if (response?.isSuccess && response?.data?.success) {
        message.success(response.message);
      } else {
        message.error(response.errorMessage);
      }
    } catch (err) {
      setIsLoading(false);
      message.error(err.message);
      return;
    } finally {
      setIsLoading(false);
      handleRemoveUpload();
    }
    handleClose();
    handleReload();
  };

  const uploadProps = {
    name: 'file',
    accept: '.pdf,',
    multiple: false,
    action: uploadPDFCallBack,
    onRemove: (file) => {
      setSignedURL(undefined);
      setUnsignedURL(undefined);
      setHideUploadModal(false);
      setFileList([]);
    },
  };

  const renderBillingCycle = () => {
    if (data?.start_date && data?.end_date) {
      const startDate = formatDateTime(data.start_date, 'DD MMM YY');
      const endDate = formatDateTime(data.end_date, 'DD MMM YY');
      return `${startDate} - ${endDate}`;
    }
    return '';
  };

  const renderTitle = () => {
    return (
      <div className={classes.drawerTitle}>
        <div className={classes.titleHeading}>
          <CloseOutlined
            onClick={() => handleClose()}
            className={classes.titleClose}
          />
          {actionTypeAndModalTitleMapping[actionType]}
        </div>
        <Button
          style={{ marginLeft: 670 }}
          type="primary"
          onClick={() => handleClose()}
        >
          Cancel
        </Button>
        <Button
          style={{ marginLeft: '4px' }}
          type="primary"
          onClick={() => handleRaiseInvoice()}
        >
          Raise
        </Button>
      </div>
    );
  };

  const getDisplayValue = (col, record) => {
    switch (col.column_id) {
      case 'net_amount':
      case 'total_tax':
      case 'final_amount':
        return formatNumber(record[col.column_id]);
      default:
        return transformName(get(record, col.column_id, '-') || '-');
    }
  };

  return (
    <Drawer
      title={renderTitle()}
      closable={false}
      onClose={() => handleClose()}
      visible={isVisible}
      width={1000}
    >
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 1 }}>
          <Row align="middle">
            <Col span={6}>Invoice No.</Col>
            <Col span={18}>
              <Input
                disabled
                className={classes.inputStyle}
                defaultValue={data?.invoice_number}
              />
            </Col>
          </Row>
          <Row align="middle" style={{ marginTop: '20px' }}>
            <Col span={6}>Billing cycle</Col>
            <Col span={18}>
              <Input
                disabled
                className={classes.inputStyle}
                defaultValue={renderBillingCycle()}
              />
            </Col>
          </Row>
          <Divider />
          <Row align="middle" style={{ marginTop: '20px' }}>
            <Col span={6}>Supporting Calculations</Col>
            <Col span={18}>
              {data?.invoice_computation_excel_url ? (
                <>
                  <a
                    href={data.invoice_computation_excel_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <DownloadOutlined style={{ marginLeft: '60px', color: 'blue' }} />
                  </a>
                  <span style={{ marginLeft: '10px' }}>Download Calculations</span>
                </>
              ) : (
                <span style={{ marginLeft: '60px', color: 'grey' }}>-</span>
              )}
            </Col>
          </Row>
          {columnsToShow?.map((col) => (
            <Row align="middle" style={{ marginTop: '20px' }} key={col.column_id}>
              <Col span={6}>
                {col.pretty_name || capitalize(col.column_id)}
              </Col>
              <Col span={18}>
                <Input
                  disabled
                  className={classes?.inputStyle}
                  style={{ width: 220, textAlign: 'right' }}
                  value={getDisplayValue(col, data)}
                />
              </Col>
            </Row>
          ))}
        </div>
        <div style={{ borderLeft: '2px solid #f0f0f0', minHeight: '100vh' }} />
        <div style={{ flex: 1 }}>
          {(!signedURL && !unsignedURL && !hideUploadModal) ? (
            <Row>
              <Col span={24}>
                <Dragger {...uploadProps} fileList={fileList} className={classes.dragger}>
                  <p>
                    {isLoading ? <Spin size="large" /> : <InboxOutlined style={{ fontSize: '24px' }} />}
                  </p>
                  <p>
                    Click or drag file to this area to upload
                  </p>
                  <p>
                    Please upload PDF files only
                  </p>
                </Dragger>
              </Col>
            </Row>
          ) : (
            <div>
              <DeleteOutlined
                style={{ fontSize: '18px', float: 'right', marginBottom: '10px' }}
                onClick={() => handleRemoveUpload()}
              />
              <iframe
                title="invoice"
                src={unsignedURL}
                width="100%"
                height="100vh"
                style={{ marginLeft: '10px', boxSizing: 'content-box', height: '80vh' }}
              />
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default withStyles(ReceivablesRequestActionStyles)(RaiseInvoiceActionDrawer);
